import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { GroupCard } from './GroupCard';
import { CreateGroupDialog } from './CreateGroupDialog';
import { fetchAllBotsWinRateStats } from '@/services/conditional-skip-stats-service';

interface StrategyGroup {
  id: string;
  name: string;
  description: string;
  group_type: 'TREND' | 'SIDEWAYS' | 'CUSTOM';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const StrategyGroupManager: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // CONSOLIDATED QUERY 1: Fetch all strategy groups with members
  const { data: groups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['strategy-groups'],
    queryFn: async () => {
      // @ts-ignore - strategy_groups table will be created by migration
      const { data, error } = await supabase
        // @ts-ignore
        .from('strategy_groups')
        .select(`
          *,
          strategy_group_members(
            id,
            bot_id,
            priority,
            is_active,
            bots(id, name, status)
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as (StrategyGroup & { strategy_group_members: any[] })[];
    },
    refetchInterval: 15000, // Reduced: 10s → 15s
  });

  // CONSOLIDATED QUERY 2: Fetch ALL group states at once
  const { data: allGroupStates, isLoading: isLoadingStates } = useQuery({
    queryKey: ['all-strategy-group-states'],
    queryFn: async () => {
      // @ts-ignore
      const { data, error } = await supabase
        // @ts-ignore
        .from('strategy_group_states')
        .select('group_id, active_bot_id, active_since');
      
      if (error) throw error;
      return new Map(data?.map(d => [d.group_id, d]) || []);
    },
    refetchInterval: 10000, // Reduced: 5s → 10s
    enabled: !!groups && groups.length > 0,
  });

  // CONSOLIDATED QUERY 3: Fetch ALL bot states at once
  const allBotIds = useMemo(() => {
    if (!groups) return [];
    return groups.flatMap(g => 
      (g.strategy_group_members || []).map(m => m.bot_id)
    );
  }, [groups]);

  const { data: allBotStates, isLoading: isLoadingBotStates } = useQuery({
    queryKey: ['all-group-bot-states', allBotIds],
    queryFn: async () => {
      if (allBotIds.length === 0) return new Map();
      
      // @ts-ignore - bot_current_condition_skip table
      const { data, error } = await supabase
        // @ts-ignore
        .from('bot_current_condition_skip')
        .select(`
          bot_id, 
          mode, 
          updated_at, 
          recent_results, 
          has_pending_non_active_enter,
          active_lock_pattern_id,
          flexible_skip_patterns(is_permanent, lock_pattern)
        `)
        .in('bot_id', allBotIds);
      
      if (error) throw error;
      
      return new Map(data?.map(d => [d.bot_id, {
        ...d,
        is_permanent_locked: (d as any).flexible_skip_patterns?.is_permanent || false,
        lock_pattern: (d as any).flexible_skip_patterns?.lock_pattern
      }]) || []);
    },
    refetchInterval: 10000, // Reduced: 5s → 10s
    enabled: allBotIds.length > 0,
  });

  // CONSOLIDATED QUERY 4: Fetch ALL win rate stats at once
  const { data: allWinRateStats, isLoading: isLoadingWinRates } = useQuery({
    queryKey: ['all-group-bot-win-rates'],
    queryFn: async () => {
      return await fetchAllBotsWinRateStats();
    },
    refetchInterval: 60000, // Reduced: 30s → 60s (win rates don't change quickly)
    enabled: allBotIds.length > 0,
  });

  // Loading state
  const isLoading = isLoadingGroups || isLoadingStates || isLoadingBotStates || isLoadingWinRates;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Strategy Groups</CardTitle>
              <CardDescription>
                Coordinate multiple bots with priority-based selection. Only one bot per group will forward signals at a time.
              </CardDescription>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Group
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Groups List */}
      {isLoading && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-96 mt-2" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-48 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!isLoading && (!groups || groups.length === 0) && (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              No strategy groups yet. Create your first group to coordinate bots by priority.
            </p>
          </CardContent>
        </Card>
      )}

      {groups && groups.length > 0 && (
        <div className="space-y-4">
          {groups.map(group => (
            <GroupCard
              key={group.id}
              group={group}
              groupState={allGroupStates?.get(group.id)}
              botStates={allBotStates}
              winRateStats={allWinRateStats}
              onUpdate={() => {
                queryClient.invalidateQueries({ queryKey: ['strategy-groups'] });
                queryClient.invalidateQueries({ queryKey: ['all-strategy-group-states'] });
                queryClient.invalidateQueries({ queryKey: ['all-group-bot-states'] });
              }}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <CreateGroupDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['strategy-groups'] });
          setIsCreateDialogOpen(false);
        }}
      />
    </div>
  );
};
